import { newIdempotencyKey } from './idempotency-key';
import { newUuidv7 } from './uuidv7';

/**
 * One attempt at one write, on a form that stays open and is edited between
 * attempts.
 *
 * **This is the third idempotency pattern in the app, and the other two are both
 * right where they are** — which is why it is a module with an argument rather
 * than a line copied from the dialog next door:
 *
 * - **One key per mount** — `const [key] = useState(newIdempotencyKey)`.
 *   `RepriceDialog`, `VoidTicketDialog`, `RegisterTerminalDialog`. Correct where
 *   the payload *cannot* change: the dialog unmounts on success and a refusal is
 *   corrected by reopening it, so every send is the same write and the held key
 *   is what makes a dropped answer replay instead of registering a second
 *   device.
 * - **One key per attempt** — `newIdempotencyKey()` at the call site.
 *   `Staffing`. Correct only because that route **replaces** a set rather than
 *   adding to it, so writing it twice lands the same rows. Its own note says so
 *   at length and is a warning rather than a precedent.
 *
 * An editable multi-line form is neither, and copying either one is a bug with a
 * delay on it:
 *
 * - A key held for the form's life earns `409 SYNC_IDEMPOTENCY_KEY_REUSED` the
 *   first time somebody is refused, fixes a typo and saves again — presenting the
 *   same `(scope, key)` with a different body. That refusal is **not retryable
 *   and retrying never clears it**, so the form can never be sent at all.
 * - A fresh key on every press is the quieter bug. These routes *add* to an
 *   append-only table, so a request whose answer was lost and which is sent
 *   again under a new key writes the goods twice — no error anywhere, and no way
 *   to take the second one back.
 *
 * **So the attempt belongs to the payload.** While the body stands, the key
 * stands and a retry replays; the moment the body moves, it is a different piece
 * of work and gets a fresh one.
 *
 * ## The body's own `id` is minted here, with the key
 *
 * Every one of these routes takes a client-minted `id` in its body as well as
 * the header key. Minting it separately is a trap: it is *inside* the payload,
 * so a fresh id on every press makes every retry a different body — and the
 * replay the key was held for never happens. Minted here, the id is a function
 * of the fingerprint, so two attempts that compare equal carry the same id as
 * well as the same key and the raw bodies are byte-identical.
 *
 * That is also why {@link fingerprintOf}-style builders in the forms leave the
 * `id` out: it is derived from the fingerprint rather than an input to it.
 *
 * ## Clear it after a success
 *
 * A recorded write is finished, and the next one is a different act however
 * similar it looks — two breakages of the same item on the same day are two real
 * corrections, and two identical delivery notes are two real consignments. So a
 * caller sets its held attempt back to `null` once the server has answered, and
 * the next send mints afresh rather than replaying an answer from ten minutes
 * ago.
 *
 * ## History
 *
 * Written twice, within a day, by two workers who could not see each other's
 * branches: `correction-form.ts` (FE038, #119) and `payload-key.ts` (FE037,
 * #120), as a pure function and as a hook. They agreed, which is the good case
 * and not one to rely on — `lib/field-schemas.ts` exists because two copies of
 * `PIN_MALFORMED` did the same thing and nothing in `pnpm validate` could have
 * noticed when one changed. This is the merge of the two, and the stocktake form
 * (FE039) is the third consumer rather than the third copy.
 */
export interface WriteAttempt {
  /** What this attempt is an attempt at. Two equal fingerprints are one write. */
  readonly fingerprint: string;

  /** The client-minted `id` the body carries. UUIDv7, per Principle V. */
  readonly id: string;

  /** The `Idempotency-Key` header. Held for as long as the fingerprint is. */
  readonly key: string;
}

/**
 * The attempt to send this body under — the one already in hand if it is for the
 * same body, otherwise a fresh one.
 *
 * Pure but for the two minters, and deliberately not a hook: a caller holds it
 * in a `useRef` and nothing renders from it, so there is no state to
 * synchronise and the rule can be read without a submit handler around it.
 *
 * ```ts
 * const attempt = useRef<WriteAttempt | null>(null);
 * // …
 * const sending = attemptFor(attempt.current, fingerprintOf(outletId, content));
 * attempt.current = sending;
 * // …on success:
 * attempt.current = null;
 * ```
 */
export function attemptFor(held: WriteAttempt | null, fingerprint: string): WriteAttempt {
  if (held !== null && held.fingerprint === fingerprint) return held;

  return { fingerprint, id: newUuidv7(), key: newIdempotencyKey() };
}
